import { useCallback } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useAppSelector } from '../../../../hooks';
import { environmentSelector } from '@selectors/commonSelectors';

/**
 * Hook that wraps react-google-recaptcha-v3's executeRecaptcha.
 *
 * - `isCaptchaReady`: true when captcha is hidden by env OR the SDK is loaded.
 *    Callers should disable submit while this is false.
 * - `getReCaptchaToken`: resolves to a token string, `''` when captcha is hidden by env,
 *    or `null` when the SDK is not ready or execute fails. Callers should treat `null`
 *    like a reCAPTCHA error and show `common.recaptcha.verificationFailed` (ErrorModal).
 */
export const useReCaptcha = () => {
    const { hideCaptcha } = useAppSelector(environmentSelector);
    const { executeRecaptcha } = useGoogleReCaptcha();

    const isCaptchaReady = hideCaptcha || !!executeRecaptcha;

    const getReCaptchaToken = useCallback(
        async (action: string): Promise<string | null> => {
            if (hideCaptcha) {
                return '';
            }
            if (!executeRecaptcha) {
                return null;
            }
            try {
                return await executeRecaptcha(action);
            } catch {
                return null;
            }
        },
        [hideCaptcha, executeRecaptcha],
    );

    return { getReCaptchaToken, isCaptchaReady };
};
